import React, { useEffect, useState } from 'react';
import AggregationControl from '../../ControlPanel/AggregationControl';
import NPIBadge from '../../NPIBadge/NPIBadge';
import { apiPost } from '../../../services_1/apiClient';
import './AggregationWindow.css';

const AggregationWindow = ({ onStateChange }) => {
  const [loading, setLoading] = useState(false);
  const [aggregationData, setAggregationData] = useState(null);
  const [drillData, setDrillData] = useState(null);
  const [error, setError] = useState(null);
  const [selectedSkuId, setSelectedSkuId] = useState('');

  useEffect(() => {
    if (typeof onStateChange === 'function') {
      onStateChange({
        loading,
        aggregationData,
        drillData,
        error,
        selectedSkuId,
      });
    }
  }, [loading, aggregationData, drillData, error, selectedSkuId, onStateChange]);

  const handleAggregate = async (params) => {
    setSelectedSkuId(params.sku_id || '');
    setLoading(true);
    setError(null);
    setDrillData(null);

    try {
      const data = await apiPost('/api/aggregate-forecast', params);
      setAggregationData(data);
    } catch (requestError) {
      setError(`Failed to generate aggregated forecast: ${requestError.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDrillDown = async (drillRequest) => {
    setLoading(true);
    setError(null);

    try {
      const data = await apiPost('/api/drill-down', drillRequest);
      setDrillData(data);
      setAggregationData(null);
    } catch (requestError) {
      setError(`Failed to drill down: ${requestError.message}`);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined || Number.isNaN(Number(num))) return '0';
    return Number(num).toLocaleString();
  };

  const formatPercent = (num) => {
    if (num === null || num === undefined || Number.isNaN(Number(num))) return '0%';
    return `${Number(num).toFixed(1)}%`;
  };

  const formatDrillLevel = (level = '') =>
    String(level)
      .split('_')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join('_');

  const getDrillCardTitle = (result = {}) =>
    formatDrillLevel(result.level || drillData?.target_level || '');

  const getDrillSubtitle = (result, metadata) => {
    const filters = metadata?.filters_applied || {};
    const drillDim = result?.drill_context?.drill_dimension;
    const drillValue = result?.drill_context?.drill_value;

    const parts = [];
    if (drillDim === 'brand' && drillValue) {
      parts.push(drillValue);
    }
    if (filters.brand && drillDim !== 'brand') parts.push(filters.brand);
    if (filters.location) parts.push(filters.location);
    if (filters.channel) parts.push(filters.channel);

    if (drillDim === 'location' && drillValue) {
      parts.push(drillValue);
    }
    if (drillDim === 'sku_id' && drillValue) {
      parts.push(drillValue);
    }

    const deduped = [...new Set(parts.filter(Boolean))];
    if (deduped.length) {
      return deduped.slice(0, 4).join(' · ');
    }

    const summary = result?.summary_metrics || {};
    const dm = summary?.demand_metrics || {};
    if (dm && (dm.total_demand || dm.average_daily_demand)) {
      return [
        dm.total_demand ? `${formatNumber(dm.total_demand)} total demand` : null,
        dm.average_daily_demand ? `${formatNumber(dm.average_daily_demand, 1)} avg/day` : null,
      ].filter(Boolean).join(' · ');
    }

    return 'Drill result';
  };

  const renderForecastPreview = (forecastData) => {
    if (!forecastData?.forecast_values?.length) return null;

    const values = forecastData.forecast_values;
    const dates = forecastData.dates || [];

    return (
      <div className="forecast-preview">
        <h4>Forecast Preview</h4>
        <div className="forecast-values">
          {values.slice(0, 7).map((value, index) => (
            <div key={`${dates[index] || index}-${value}`} className="forecast-value">
              <span className="forecast-day">
                {dates[index] ? dates[index].slice(5) : `Day ${index + 1}`}
              </span>
              <strong>{formatNumber(value)}</strong>
            </div>
          ))}
        </div>
        {values.every((value) => Number(value) === 0) && (
          <div className="forecast-note">
            Note: Forecast values are 0. This usually means the selected filters did not find a usable demand slice.
          </div>
        )}
      </div>
    );
  };

  const summaryMetrics = aggregationData?.summary_metrics || {};
  const demandMetrics = summaryMetrics?.demand_metrics || {};
  const dateRange = summaryMetrics?.date_range || null;
  const forecastValueCount = aggregationData?.forecast_data?.forecast_values?.length || 0;

  return (
    <div className="aggregation-window">
      <AggregationControl
        onAggregate={handleAggregate}
        onDrillDown={handleDrillDown}
        loading={loading}
      />

      {loading && (
        <div className="aggregation-loading-overlay" role="status" aria-live="polite" aria-busy="true">
          <div className="aggregation-loading-card">
            <div className="aggregation-loading-spinner" />
            <strong>Aggregating forecast data...</strong>
            <span>Please wait while the forecast is being generated.</span>
          </div>
        </div>
      )}

      {error && (
        <div className="error-message">
          <div className="error-content">
            <h4>Error</h4>
            <p>{error}</p>
            <button
              onClick={() => setError(null)}
              className="dismiss-button"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {selectedSkuId && <NPIBadge skuId={selectedSkuId} />}

      {(aggregationData || drillData) && (
        <section className="card">
          <div className="section-title aggregation-section-title">
            <div>
              <h2>Multi-Level Forecast Analysis</h2>
              <p>Aggregation output stays in the same layout while the values refresh from your filters.</p>
            </div>
            {aggregationData?.level && (
              <span className="aggregation-level-pill">
                {aggregationData.level.replace(/_/g, ' ')}
              </span>
            )}
          </div>

          <div className="results-container">
            {aggregationData && (
              <div className="aggregation-results">
                <h3>Aggregation Summary</h3>

                <div className="results-grid">
                  <div className="result-item">
                    <strong>Level</strong>
                    <span>{aggregationData.level}</span>
                  </div>
                  <div className="result-item">
                    <strong>Records</strong>
                    <span>{formatNumber(summaryMetrics.total_records || 0)}</span>
                  </div>
                  <div className="result-item">
                    <strong>Quality Score</strong>
                    <span>{formatPercent(aggregationData?.data_quality?.overall_score || 0)}</span>
                  </div>
                  <div className="result-item">
                    <strong>Forecast Days</strong>
                    <span>{forecastValueCount}</span>
                  </div>
                  <div className="result-item">
                    <strong>Total Demand</strong>
                    <span>{formatNumber(demandMetrics.total_demand || 0)}</span>
                  </div>
                  <div className="result-item">
                    <strong>Avg Daily Demand</strong>
                    <span>{formatNumber(demandMetrics.average_daily_demand || 0)}</span>
                  </div>
                  {dateRange && (
                    <div className="result-item result-item-wide">
                      <strong>Date Range</strong>
                      <span>{dateRange.start_date} to {dateRange.end_date}</span>
                    </div>
                  )}
                </div>

                {renderForecastPreview(aggregationData.forecast_data)}

                {(aggregationData.drill_down_options?.length > 0 || aggregationData.roll_up_options?.length > 0) && (
                  <div className="drill-options-display">
                    <h4>Available Navigation</h4>
                    {aggregationData.drill_down_options?.length > 0 && (
                      <p><strong>Drill Down to:</strong> {aggregationData.drill_down_options.map((opt) => opt.display_name).join(', ')}</p>
                    )}
                    {aggregationData.roll_up_options?.length > 0 && (
                      <p><strong>Roll Up to:</strong> {aggregationData.roll_up_options.map((opt) => opt.display_name).join(', ')}</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {drillData && (
              <div className="drill-results">
                <h3>Drill Results - {formatDrillLevel(drillData.target_level || '')}</h3>
                <div className="drill-summary">
                  Found {drillData.drill_results?.length || 0} drill results
                </div>

                <div className="drill-grid">
                  {drillData.drill_results?.map((result, index) => (
                    <div key={`${result.aggregation_key}-${index}`} className="drill-item">
                      <div className="drill-item-head">
                        <strong>{getDrillCardTitle(result)}</strong>
                        <div className="drill-item-stats">
                          <span>Records: {formatNumber(result.summary_metrics?.total_records || 0)}</span>
                          {result.summary_metrics?.demand_metrics && (
                            <span>Total Demand: {formatNumber(result.summary_metrics.demand_metrics.total_demand || 0)}</span>
                          )}
                        </div>
                      </div>
                      <div className="drill-item-sub">
                        {getDrillSubtitle(result, drillData.metadata)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {!loading && !aggregationData && !drillData && !error && (
        <section className="card">
          <div className="welcome-section compact">
            <h2 className="section-title">Multi-Level Aggregation</h2>
            <p className="aggregation-intro">
              Choose a level, add any filters you need, and generate the forecast. The result view will stay in the same structured format.
            </p>
            <div className="getting-started">
              <h3>Quick Start</h3>
              <ol>
                <li>Select the aggregation level you want.</li>
                <li>Apply brand, location, channel, or customer filters if needed.</li>
                <li>Run the aggregation and use drill-down or roll-up from the same page.</li>
              </ol>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default AggregationWindow;
